import type { AxiosInstance } from 'axios';

export type ReferralCampaignStatus = 'DRAFT' | 'ACTIVE' | 'PAUSED' | 'ENDED';
export type ReferralRewardKind = 'REFERRER_CASHBACK' | 'REFEREE_CASHBACK' | 'MLM_COMMISSION';
export type ReferralRewardStatus = 'PENDING' | 'ISSUED' | 'REVERSED' | 'EXPIRED';
export type TeamStatus = 'ACTIVE' | 'ARCHIVED';
export type TeamMembershipRole = 'LEADER' | 'MEMBER';
export type RankTierCode = 'BRONZE' | 'SILVER' | 'GOLD' | 'PLATINUM' | 'DIAMOND';

export interface ReferralCampaign {
  id: string;
  code: string;
  name: string;
  status: ReferralCampaignStatus;
  referrerRewardPaise: number;
  refereeRewardPaise: number;
  minOrderValuePaise: number;
  maxReferralsPerReferrer: number;
  rewardExpiryDays: number;
  priority: number;
  startsAt: string;
  endsAt: string | null;
  notes: string | null;
  createdAt: string;
}

export interface MyReferralCode {
  userId: string;
  code: string;
  createdAt: string;
  totalClicks: number;
  totalConversions: number;
  totalRewardPaise: number;
}

export interface ReferralReward {
  id: string;
  campaignId: string | null;
  conversionId: string | null;
  beneficiaryId: string;
  kind: ReferralRewardKind;
  status: ReferralRewardStatus;
  amountPaise: number;
  walletCreditId: string | null;
  mlmLevel: number | null;
  notes: string | null;
  createdAt: string;
}

export interface Team {
  id: string;
  code: string;
  name: string;
  leaderUserId: string;
  status: TeamStatus;
  gmvLifetimePaise: number;
  gmv30dPaise: number;
  activeMemberCount: number;
  salesGoalPaise: number;
  goalCycle: string;
  createdAt: string;
}

export interface TeamMembership {
  id: string;
  teamId: string;
  userId: string;
  role: TeamMembershipRole;
  joinedAt: string;
  leftAt: string | null;
}

export interface RankTier {
  id: string;
  code: RankTierCode;
  ordinal: number;
  minGmvLifetimePaise: number;
  minDirectReferrals: number;
  commissionMultiplierBps: number;
  active: boolean;
}

export interface UserRank {
  userId: string;
  tierCode: RankTierCode;
  gmvLifetimePaise: number;
  directReferrals: number;
  promotedAt: string | null;
  computedAt: string;
}

export interface MlmCommissionRule {
  id: string;
  level: number;
  rateBps: number;
  active: boolean;
}

export interface RankEngineResult {
  scanned: number;
  promotedCount: number;
  demotedCount: number;
  durationMs: number;
}

export function networkEndpoints(http: AxiosInstance) {
  return {
    // Self-service
    myCode: () => http.get<MyReferralCode>('/network/referral/me').then((r) => r.data),
    recordClick: (body: { code: string; anonymousId?: string; source?: string }) =>
      http.post('/network/referral/click', body).then((r) => r.data),
    attachReferral: (code: string) =>
      http.post('/network/referral/attach', { code }).then((r) => r.data),
    myRewards: () =>
      http.get<{ data: ReferralReward[] }>('/network/rewards/me').then((r) => r.data.data),
    myRank: () => http.get<UserRank>('/network/rank/me').then((r) => r.data),
    myTeams: () => http.get<{ data: Team[] }>('/network/teams/me').then((r) => r.data.data),

    // Admin — campaigns
    listCampaigns: () =>
      http.get<{ data: ReferralCampaign[] }>('/admin/network/campaigns').then((r) => r.data.data),
    upsertCampaign: (body: {
      code: string;
      name: string;
      referrerRewardPaise: number;
      refereeRewardPaise: number;
      minOrderValuePaise?: number;
      maxReferralsPerReferrer?: number;
      rewardExpiryDays?: number;
      priority?: number;
      status?: ReferralCampaignStatus;
    }) => http.put<ReferralCampaign>('/admin/network/campaigns', body).then((r) => r.data),

    // Admin — teams
    listTeams: () => http.get<{ data: Team[] }>('/admin/network/teams').then((r) => r.data.data),
    getTeam: (teamId: string) =>
      http
        .get<Team & { members: TeamMembership[] }>(`/admin/network/teams/${teamId}`)
        .then((r) => r.data),
    createTeam: (body: {
      code: string;
      name: string;
      leaderUserId: string;
      salesGoalPaise?: number;
      goalCycle?: 'DAY' | 'WEEK' | 'MONTH' | 'QUARTER' | 'YEAR';
    }) => http.post<Team>('/admin/network/teams', body).then((r) => r.data),
    addMember: (teamId: string, body: { userId: string; role?: TeamMembershipRole }) =>
      http.post(`/admin/network/teams/${teamId}/members`, body).then((r) => r.data),
    refreshTeamGmv: (teamId: string) =>
      http.post<Team>(`/admin/network/teams/${teamId}/refresh`).then((r) => r.data),

    // Admin — ranks
    listRankTiers: () =>
      http.get<{ data: RankTier[] }>('/admin/network/rank-tiers').then((r) => r.data.data),
    upsertRankTier: (body: {
      code: RankTierCode;
      ordinal: number;
      minGmvLifetimePaise: number;
      minDirectReferrals: number;
      commissionMultiplierBps: number;
      active?: boolean;
    }) => http.put<RankTier>('/admin/network/rank-tiers', body).then((r) => r.data),
    runRankEngine: (limit = 1000) =>
      http.post<RankEngineResult>('/admin/network/rank/engine/run', { limit }).then((r) => r.data),

    // Admin — MLM rules
    listMlmRules: () =>
      http.get<{ data: MlmCommissionRule[] }>('/admin/network/mlm/rules').then((r) => r.data.data),
    upsertMlmRule: (body: { level: number; rateBps: number; active?: boolean; notes?: string }) =>
      http.put<MlmCommissionRule>('/admin/network/mlm/rules', body).then((r) => r.data),
  };
}

export type NetworkEndpoints = ReturnType<typeof networkEndpoints>;
